/**
 * ProfileCover — the cover section at the top of the unified Profile
 * card. Renders the avatar + identity stack + "Edit name" action, with
 * a thin in-section footer for the avatar upload status / helper.
 *
 * Returns just the section content (no Card wrapper) — the route owns
 * the outer Card and stacks every section with hairline dividers via
 * `divide-y`.
 */
import * as React from "react";
import { Pencil } from "lucide-react";
import { Button, StatusPill } from "@flint/ui";
import type { MeUser, MeCompany } from "../../lib/session.ts";
import { formatDate, formatPhoneIN } from "../../lib/format.ts";
import {
  AvatarUploader,
  avatarStatusMessage,
  type AvatarUploadStatus,
} from "./avatar-uploader.tsx";

export interface ProfileCoverProps {
  user: MeUser;
  company: MeCompany;
  onEditName: () => void;
}

export function ProfileCover({ user, company, onEditName }: ProfileCoverProps) {
  const [uploadStatus, setUploadStatus] = React.useState<AvatarUploadStatus>({ kind: "idle" });
  const status = avatarStatusMessage(uploadStatus);

  return (
    <section>
      {/* Identity row. `items-start` so a wrapped name (long name, narrow
          viewport) doesn't vertically pull the avatar around. */}
      <div className="flex items-start gap-5 p-5 sm:p-6">
        {/* Fixed-width avatar column — the avatar's footprint plus a
            hair for the camera chip's outset ring. */}
        <div className="shrink-0">
          <AvatarUploader user={user} onStatusChange={setUploadStatus} />
        </div>

        {/* Identity stack. `min-w-0` lets long emails truncate cleanly. */}
        <div className="min-w-0 flex-1 flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <h1 className="text-xl font-semibold text-ink leading-tight truncate">{user.name}</h1>
            {company ? (
              <StatusPill tone="sky" size="sm">
                {company.role}
              </StatusPill>
            ) : null}
          </div>

          <div className="text-sm text-ink-2 num">{formatPhoneIN(user.phone)}</div>

          {user.email ? <div className="text-sm text-muted truncate">{user.email}</div> : null}

          <div className="text-sm text-muted mt-0.5">
            Member since {formatDate(user.createdAt)}
          </div>
        </div>

        {/* Top-right action — aligned with the identity headline. */}
        <div className="shrink-0">
          <Button intent="ghost" size="sm" onClick={onEditName}>
            <Pencil size={14} strokeWidth={2} aria-hidden />
            <span className="hidden sm:inline">Edit name</span>
          </Button>
        </div>
      </div>

      {/* In-section footer band — divided from the identity row so it
          reads as the avatar-upload status surface. */}
      <div className="border-t border-line px-5 sm:px-6 py-3 flex items-center justify-between gap-3">
        <span className={`text-sm ${status.tone === "bad" ? "text-bad" : "text-muted"}`}>
          {status.text}
        </span>
      </div>
    </section>
  );
}
